import { useEffect, useMemo, useState } from "react";
import { Layers3, Save } from "lucide-react";

import { api, apiError } from "@/lib/api";

const textureOptions = [
  { value: "solid", label: "Color liso" },
  { value: "paver", label: "Adoquín PAVIC" },
  { value: "natural", label: "Tierra natural" },
  { value: "waves", label: "Olas" },
  { value: "trees", label: "Arbolado" },
  { value: "hatch", label: "Trama técnica" },
];

export default function PlanSettingsPanel() {
  const [layers, setLayers] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/settings/plan")
      .then((response) => setLayers(response.data.layers))
      .catch(() => setError("No fue posible cargar las capas del plano."))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    return layers.reduce((groups, layer) => {
      groups[layer.category] = [...(groups[layer.category] || []), layer];
      return groups;
    }, {});
  }, [layers]);

  const toggleLayer = (layerId) => {
    setLayers((current) => current.map((layer) => (
      layer.id === layerId ? { ...layer, enabled: !layer.enabled } : layer
    )));
  };

  const setLayerZoom = (layerId, minZoom) => {
    setLayers((current) => current.map((layer) => (
      layer.id === layerId ? { ...layer, min_zoom: Number(minZoom) } : layer
    )));
  };

  const setLayerStyle = (layerId, field, value) => {
    setLayers((current) => current.map((layer) => (
      layer.id === layerId ? { ...layer, [field]: value } : layer
    )));
  };

  const save = async () => {
    try {
      const response = await api.put("/settings/plan", {
        layers: Object.fromEntries(layers.map((layer) => [layer.id, {
          enabled: layer.enabled,
          min_zoom: layer.min_zoom,
          color: layer.color,
          texture: layer.texture,
          opacity: layer.opacity,
        }])),
      });
      setLayers(response.data.layers);
      setMessage("Capas, colores y texturas del plano actualizados.");
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  return (
    <section className="plan-settings-panel" data-testid="plan-settings-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">CARTOGRAFÍA</span>
          <h2>Capas del plano</h2>
        </div>
        <Layers3 size={21} />
      </div>
      <p>
        Define visibilidad, acercamiento, color, textura y transparencia para cada capa del plano.
      </p>
      {loading && <p data-testid="plan-settings-loading">Cargando capas cartográficas...</p>}
      {error && <p className="form-error" data-testid="plan-settings-error">{error}</p>}
      {!loading && !error && <div className="plan-layer-groups">
        {Object.entries(grouped).map(([category, group]) => (
          <div className="plan-layer-group" key={category}>
            <strong>{category}</strong>
            {group.map((layer) => (
              <div className="plan-layer-row" key={layer.id}>
                <div className="plan-layer-name">
                  <label className="plan-layer-toggle">
                    <input
                      checked={layer.enabled}
                      data-testid={`plan-layer-toggle-${layer.id}`}
                      onChange={() => toggleLayer(layer.id)}
                      type="checkbox"
                    />
                    <span>{layer.label}</span>
                  </label>
                  <div className="plan-layer-controls">
                    <input
                      aria-label={`Color de ${layer.label}`}
                      data-testid={`plan-layer-color-${layer.id}`}
                      onChange={(event) => setLayerStyle(layer.id, "color", event.target.value)}
                      type="color"
                      value={layer.color}
                    />
                    <select
                      aria-label={`Textura de ${layer.label}`}
                      data-testid={`plan-layer-texture-${layer.id}`}
                      onChange={(event) => setLayerStyle(layer.id, "texture", event.target.value)}
                      value={layer.texture}
                    >
                      {textureOptions.map((texture) => (
                        <option key={texture.value} value={texture.value}>
                          {texture.label}
                        </option>
                      ))}
                    </select>
                    <label className="plan-opacity-control">
                      <span>Opacidad</span>
                      <input
                        aria-label={`Opacidad de ${layer.label}`}
                        data-testid={`plan-layer-opacity-${layer.id}`}
                        max="1"
                        min="0.15"
                        onChange={(event) => setLayerStyle(
                          layer.id,
                          "opacity",
                          Number(event.target.value),
                        )}
                        step="0.05"
                        type="range"
                        value={layer.opacity}
                      />
                    </label>
                  </div>
                </div>
                <select
                  aria-label={`Zoom de ${layer.label}`}
                  data-testid={`plan-layer-zoom-${layer.id}`}
                  onChange={(event) => setLayerZoom(layer.id, event.target.value)}
                  value={layer.min_zoom}
                >
                  <option value="1">Siempre</option>
                  <option value="2.5">Al acercar</option>
                  <option value="5">Detalle</option>
                  <option value="7">Muy cerca</option>
                </select>
              </div>
            ))}
          </div>
        ))}
      </div>}
      <button
        className="primary-button"
        data-testid="save-plan-layers-button"
        disabled={loading || Boolean(error)}
        onClick={save}
        type="button"
      >
        <Save size={16} /> Guardar capas globales
      </button>
      {message && <p className="form-note" data-testid="plan-settings-message">{message}</p>}
    </section>
  );
}
